'use client';

import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import { Box, Button, Dialog, Theme, Typography } from '@mui/material';
import { withClarityTheme } from '@verifime/styles';
import { ElementType, ReactElement, useId } from 'react';
import {
  resolveDismissRequestDialogCopy,
  type DismissRequestDialogCopy,
  type DismissRequestDialogCopyOverrides,
} from './dismissRequestDialogCopy';

export type { DismissRequestDialogCopy, DismissRequestDialogCopyOverrides };

export type DismissRequestDialogProps = {
  open: boolean;
  /** Who is asking. Named in the title. */
  providerName: string;
  /** The customer declines the request for good. */
  onDecline: () => void;
  /** The customer leaves the request open: "go back", Escape or a tap on the backdrop. */
  onCancel: () => void;
  /** Replaces any of the default strings. Strings left out keep their default. */
  copy?: DismissRequestDialogCopyOverrides;
  'data-testid'?: string;
};

type ConsequenceKey = 'noService' | 'obligations' | 'newInvite';

// The three consequences, in the order the board draws them. The first two are warnings. The third
// is information. The board draws all three icons in the danger colour, and so does this.
const CONSEQUENCES: ReadonlyArray<{ key: ConsequenceKey; Icon: ElementType }> = [
  { key: 'noService', Icon: WarningIcon },
  { key: 'obligations', Icon: WarningIcon },
  { key: 'newInvite', Icon: InfoIcon },
];

// The board draws the buttons 44px high.
const buttonSx = (theme: Theme) => ({ minHeight: theme.requestActions.buttonHeight });

// 520 wide from `md` up. Below `md` the dialog takes the width the screen leaves after a gutter.
const paperSx = (theme: Theme) => {
  const gutter = theme.semantic.spacing.base;
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.semantic.spacing.base,
    boxSizing: 'border-box',
    margin: gutter,
    padding: theme.semantic.spacing['2lg'],
    borderRadius: theme.semantic.borderRadius.lg,
    width: theme.requestDialog.width,
    maxWidth: `calc(100% - 2 * ${gutter})`,
    [theme.breakpoints.down('md')]: { width: '100%' },
  };
};

// Right-aligned in a row from `md` up. Stacked and full width below it, in the same order.
const actionsSx = (theme: Theme) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.semantic.spacing.sm,
  marginTop: theme.semantic.spacing.xs,
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
});

/**
 * The dialog that asks the customer to confirm declining a request. It states the consequences
 * first. "Go back" is the safe action: it is the filled primary and has focus when the dialog opens.
 * "Decline" is the red outlined one. "Go back", Escape and a tap on the backdrop all call
 * `onCancel`. Only "decline" calls `onDecline`.
 *
 * Presentational: the parent holds `open`. It closes the dialog on `onCancel` and on `onDecline`.
 */
function DismissRequestDialogComponent({
  open,
  providerName,
  onDecline,
  onCancel,
  copy: copyOverrides,
  'data-testid': dataTestId,
}: Readonly<DismissRequestDialogProps>): ReactElement {
  const copy = resolveDismissRequestDialogCopy(copyOverrides);
  const titleId = useId();
  const consequencesId = useId();

  return (
    <Dialog
      open={open}
      onClose={() => onCancel()}
      aria-labelledby={titleId}
      aria-describedby={consequencesId}
      data-testid={dataTestId}
      PaperProps={{ sx: paperSx }}
    >
      <Typography id={titleId} variant="h5" component="h2" sx={{ overflowWrap: 'anywhere' }}>
        {copy.title(providerName)}
      </Typography>
      <Box
        id={consequencesId}
        component="ul"
        role="list"
        sx={(theme: Theme) => ({
          display: 'flex',
          flexDirection: 'column',
          gap: theme.semantic.spacing.sm,
          margin: 0,
          padding: 0,
          listStyle: 'none',
        })}
      >
        {CONSEQUENCES.map(({ key, Icon }) => (
          <Box
            key={key}
            component="li"
            sx={(theme: Theme) => ({
              display: 'flex',
              alignItems: 'flex-start',
              gap: theme.semantic.spacing.sm,
            })}
          >
            <Icon fontSize="small" sx={(theme: Theme) => ({ color: theme.palette.error.main })} />
            <Typography variant="body1" component="span">
              {copy[key]}
            </Typography>
          </Box>
        ))}
      </Box>
      <Box sx={actionsSx}>
        <Button
          variant="outlined"
          color="error"
          size="large"
          sx={buttonSx}
          data-action="dismiss-dialog-decline"
          onClick={() => onDecline()}
        >
          {copy.decline}
        </Button>
        <Button
          variant="contained"
          size="large"
          sx={buttonSx}
          data-action="dismiss-dialog-go-back"
          autoFocus
          onClick={() => onCancel()}
        >
          {copy.goBack}
        </Button>
      </Box>
    </Dialog>
  );
}

export const DismissRequestDialog = withClarityTheme(DismissRequestDialogComponent);
